import React from 'react'
import { Row, Col, FormControl, FormSelect, Form } from 'react-bootstrap'
import { BuildingFillGear } from 'react-bootstrap-icons'
import { CompanyForm } from '../types/stammdaten.type'
import { t } from '../locale'
import KDayPicker from '../components/KDayPicker'

interface TopSectionProps {
  companyData: CompanyForm
  handleChangeLocal: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void
  anredeOptions: any[]
  legalForms: any[]
  requiredFields: any
  history: (field: string, label: string, options?: any) => void
  datePickerClientSince: boolean
  datePickerClientTo: boolean
  setDatePickerClientSince: (v: boolean) => void
  setDatePickerClientTo: (v: boolean) => void
  handleDateChange: (field: string, date: Date | null) => void
  ref: any
  handleDateInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const TopSection = React.memo(
  ({
    companyData,
    handleChangeLocal,
    handleDateInputChange,
    anredeOptions,
    legalForms,
    requiredFields,
    history,
    datePickerClientSince,
    datePickerClientTo,
    setDatePickerClientSince,
    setDatePickerClientTo,
    handleDateChange,
    ref,
  }: TopSectionProps) => {
    return (
      <>
        {/* Mandanten-Nr. */}
        <Row className='mt-4'>
          <Col className='col-sm-2'>
            <label className='form-control-label'>{t('mandantNumber')}</label>
          </Col>
          <Col>
            <div className='form-group'>
              <FormControl
                className='w-25 d-inline'
                type='text'
                id='clientNumber'
                value={companyData.clientNumber}
                onChange={handleChangeLocal}
              />
            </div>
          </Col>
        </Row>

        <hr />

        <Row className='mt-4'>
          <Col className='col-sm-2'>
            <label className='form-control-label'>{t('client')}</label>
          </Col>
          <Col>
            <Row ref={ref}>
              <Col className='col-sm-2'>
                <Row className='g-1 align-items-center'>
                  <Col xs='auto' className='p-2'>
                    <label
                      className={`form-control-label mb-0 ${requiredFields.client_since ? 'required' : ''}`}
                    >
                      {t('seit')}
                    </label>
                  </Col>
                  <Col xs='auto' className='p-0'>
                    <button
                      type='button'
                      className='btn btn-link btn-sm p-0 ms-1'
                      onClick={() => history('stammdaten.client_since', 'Client since')}
                    >
                      <i className='fa fa-history text-dark' aria-hidden='true'></i>
                    </button>
                  </Col>
                </Row>
                <Form.Control
                  type='text'
                  id='client_since'
                  name='seit'
                  className='w-100 d-inline'
                  value={companyData.client_since || ''}
                  onClick={() => setDatePickerClientSince(!datePickerClientSince)}
                  onChange={handleDateInputChange}
                  placeholder='DD.MM.YYYY'
                  inputMode='numeric'
                  maxLength={10}
                  required={requiredFields.client_since}
                />
                <KDayPicker
                  open={datePickerClientSince}
                  value={companyData.client_since}
                  required={requiredFields.client_since}
                  onSelect={d => {
                    handleDateChange('client_since', d)
                    setDatePickerClientSince(false)
                  }}
                />
              </Col>
              <Col className='col-sm-2'>
                <Row className='g-1 align-items-center'>
                  <Col xs='auto' className='p-2'>
                    <label
                      className={`form-control-label mb-0 ${requiredFields.client_to ? 'required' : ''}`}
                    >
                      {t('bis')}
                    </label>
                  </Col>
                  <Col xs='auto' className='p-0'>
                    <button
                      type='button'
                      className='btn btn-link btn-sm'
                      onClick={() => history('stammdaten.client_to', 'Client to')}
                    >
                      <i className='fa fa-history text-dark' aria-hidden='true'></i>
                    </button>
                  </Col>
                </Row>
                <Form.Control
                  type='text'
                  id='client_to'
                  name='bis'
                  className='w-100 d-inline'
                  value={companyData.client_to || ''}
                  onClick={() => setDatePickerClientTo(!datePickerClientTo)}
                  onChange={handleDateInputChange}
                  placeholder='DD.MM.YYYY'
                  inputMode='numeric'
                  maxLength={10}
                  required={requiredFields.client_to}
                />
                <KDayPicker
                  open={datePickerClientTo}
                  value={companyData.client_to}
                  required={requiredFields.client_to}
                  onSelect={d => {
                    handleDateChange('client_to', d)
                    setDatePickerClientTo(false)
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        {/* anrede */}
        <h6 className='bg-gray-200 p-2 rounded mb-3 mt-3'>
          <BuildingFillGear />
          {t('Angaben')}
        </h6>
        <Row className='mt-4'>
          <Col className='col-sm-2'>
            <label className={`form-control-label ${requiredFields.anrede ? 'required' : ''}`}>
              {t('Anrede')}
            </label>
          </Col>
          <Col>
            <div className='form-group'>
              <FormSelect
                name={t('Anrede')}
                className='w-25 d-inline'
                id='anrede'
                value={companyData.anrede}
                required={requiredFields.anrede}
                onChange={handleChangeLocal}
              >
                <option value=''>{'-- '}</option>
                {anredeOptions.map((opt: any, idx) => (
                  <option key={idx} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </FormSelect>
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => history('stammdaten.anrede', 'Anrede', anredeOptions)}
              >
                <i className='fa fa-history text-dark' aria-hidden='true'></i>
              </button>
            </div>
          </Col>
        </Row>
        {/* unthersname */}
        <Row className='mt-4'>
          <Col className='col-sm-2'>
            <label className='form-control-label required'>{t('Unternehmensname')}</label>
          </Col>
          <Col>
            <div className='form-group'>
              <FormControl
                name={t('Unternehmensname')}
                className='w-25 d-inline'
                type='text'
                id='name'
                required={true}
                value={companyData.name}
                onChange={handleChangeLocal}
              ></FormControl>
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => history('stammdaten.name', t('Unternehmensname'))}
              >
                <i className='fa fa-history text-dark' aria-hidden='true'></i>
              </button>
            </div>
          </Col>
        </Row>
        {/* Unternehmensform */}
        <Row className='mt-4'>
          <Col className='col-sm-2'>
            <label
              className={`form-control-label ${requiredFields.unternehmensform ? 'required' : ''}`}
            >
              {t('Unternehmensform')}
            </label>
          </Col>
          <Col>
            <div className='form-group'>
              <FormSelect
                name={t('Unternehmensform')}
                className='w-25 d-inline'
                id='legal_form'
                value={companyData.legal_form}
                required={requiredFields.unternehmensform}
                onChange={handleChangeLocal}
              >
                <option value=''>{'-- '}</option>
                {legalForms.map((opt: any, idx) => (
                  <option key={idx} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </FormSelect>
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => history('stammdaten.legal_form', t('Unternehmensform'), legalForms)}
              >
                <i className='fa fa-history text-dark' aria-hidden='true'></i>
              </button>
            </div>
          </Col>
        </Row>
        {/* Notiz */}
        <Row className='mt-4'>
          <Col className='col-sm-2'>
            <label className={`form-control-label ${requiredFields.client_note ? 'required' : ''}`}>
              {t('Notiz')}
            </label>
          </Col>
          <Col>
            <div className='form-group'>
              <input
                name={t('Notiz')}
                className='form-control w-50 d-inline'
                id='client_note'
                value={companyData.client_note}
                required={requiredFields.client_note}
                maxLength={40}
                onChange={handleChangeLocal}
              ></input>
              <button
                type='button'
                className='btn btn-link btn-sm'
                onClick={() => history('stammdaten.client_note', t('Notiz'))}
              >
                <i className='fa fa-history text-dark' aria-hidden='true'></i>
              </button>
            </div>
          </Col>
        </Row>
      </>
    )
  },
  (prev, next) => {
    // Vraća true = NE renderuj ponovo
    return (
      prev.companyData.clientNumber === next.companyData.clientNumber &&
      prev.companyData.client_since === next.companyData.client_since &&
      prev.companyData.client_to === next.companyData.client_to &&
      prev.companyData.anrede === next.companyData.anrede &&
      prev.companyData.name === next.companyData.name &&
      prev.companyData.legal_form === next.companyData.legal_form &&
      prev.companyData.client_note === next.companyData.client_note &&
      prev.handleChangeLocal === next.handleChangeLocal &&
      prev.handleDateInputChange === next.handleDateInputChange &&
      prev.datePickerClientSince === next.datePickerClientSince &&
      prev.datePickerClientTo === next.datePickerClientTo &&
      prev.requiredFields === next.requiredFields
    )
  }
)

export default TopSection
